export type VoiceContext =
  | { type: 'general' }
  | { type: 'create-product' }
  | { type: 'create-categories' }
  | { type: 'stock-check' }
  | { type: 'sales-summary' }
  | { type: 'conversation'; conversationId: string };

export interface VoiceContextConfig {
  greeting: { fr: string; en: string };
  promptPrefix: string | null;
}

type NonConversationContextType = Exclude<VoiceContext['type'], 'conversation'>;

/**
 * Global voice mode rules injected into every voice prompt.
 * Reduces friction by removing unnecessary confirmations for simple operations
 * and adding session completion signaling.
 */
const VOICE_MODE_RULES = `[MODE VOCAL ACTIF]

REGLES VOCALES ABSOLUES :
- Reponds en 1-3 phrases courtes, JAMAIS de listes numerotees ni de markdown.
- Parle naturellement comme un collegue, pas comme un robot.
- Pour les operations SIMPLES (ajouter/retirer stock, modifier prix, consulter infos) : AGIS DIRECTEMENT sans demander confirmation. Annonce le resultat apres.
- Pour les operations IMPORTANTES (creer un produit, enregistrer une vente, creer des categories) : fais un resume oral rapide puis demande "Je confirme ?" UNE SEULE FOIS.
- Ne repete jamais les informations que l'utilisateur vient de donner.
- Quand une tache est terminee et que l'utilisateur n'a plus rien a faire, termine ta reponse par le tag [SESSION_COMPLETE].
- Exemples de fin de session : apres avoir ajoute du stock, apres avoir donne un resume des ventes, apres une consultation simple.
- Ne mets PAS [SESSION_COMPLETE] si tu poses une question ou si tu attends une reponse.

`;

export const CONTEXT_CONFIG: Record<NonConversationContextType, VoiceContextConfig> = {
  general: {
    greeting: {
      fr: 'Comment puis-je vous aider?',
      en: 'How can I help you?',
    },
    promptPrefix:
      VOICE_MODE_RULES +
      `Tu es l'assistant vocal de la boutique. Aide le commercant avec tout ce qu'il demande : ventes, stock, clients, statistiques, rapports.

Voici ce que l'utilisateur a dit :`,
  },
  'create-product': {
    greeting: {
      fr: 'Quel produit souhaitez-vous creer?',
      en: 'What product would you like to create?',
    },
    promptPrefix:
      VOICE_MODE_RULES +
      `Tu es un assistant vocal pour creer des produits.

COMPORTEMENT :
- Si l'utilisateur donne un nom de produit, note-le et enchaine directement avec la premiere question manquante.
- Si l'utilisateur dit quelque chose de vague, demande simplement le nom du produit.
- Extrais toute info donnee spontanement (nom, prix, categorie) et ne la redemande pas.
- Regroupe les questions : "C'est un bien physique standard ? Dans quelle categorie et a quel prix ?"

INFORMATIONS A COLLECTER (saute celles deja fournies) :
- Nom, nature (bien/service), type (standard/variable/pack)
- Categorie (verifie avec get_shop_categories), marque (get_shop_brands)
- Prix de vente et d'achat (FCFA)
- Suivi de stock ?

UNE FOIS TOUT COLLECTE : resume oral rapide avec la marge, puis "Je cree le produit ?". Si oui, utilise create_product puis mets [SESSION_COMPLETE].

Voici ce que l'utilisateur a dit :`,
  },
  'create-categories': {
    greeting: {
      fr: 'Quelles categories voulez-vous creer?',
      en: 'What categories do you want to create?',
    },
    promptPrefix:
      VOICE_MODE_RULES +
      `L'utilisateur veut creer des categories par la voix.

- Confirme les categories a creer et la hierarchie (parent/enfant).
- Verifie avec get_shop_categories si elles existent deja.
- Resume rapide, puis "Je cree tout ca ?". Apres creation, mets [SESSION_COMPLETE].

Voici ce que l'utilisateur a dit :`,
  },
  'stock-check': {
    greeting: {
      fr: 'Quel produit souhaitez-vous verifier?',
      en: 'Which product would you like to check?',
    },
    promptPrefix:
      VOICE_MODE_RULES +
      `L'utilisateur veut verifier ou modifier le stock.

- Pour une CONSULTATION : utilise search_product_stock et donne le resultat directement, puis [SESSION_COMPLETE].
- Pour un AJOUT/RETRAIT de stock : utilise update_stock DIRECTEMENT sans confirmation si le produit et la quantite sont clairs. Annonce le resultat, puis [SESSION_COMPLETE].
- Si le produit est ambigu (plusieurs resultats), demande de preciser.

Voici ce que l'utilisateur a dit :`,
  },
  'sales-summary': {
    greeting: {
      fr: 'Je prepare votre resume des ventes.',
      en: 'Preparing your sales summary.',
    },
    promptPrefix:
      VOICE_MODE_RULES +
      `L'utilisateur veut un resume des ventes.

Utilise get_daily_summary et get_daily_sales pour le jour (sauf si autre periode demandee). Donne le chiffre d'affaires, nombre de ventes, top produits. Sois concis. Termine par [SESSION_COMPLETE] sauf si l'utilisateur pose une question de suivi.

Voici ce que l'utilisateur a dit :`,
  },
};

export function getContextConfig(context: VoiceContext): VoiceContextConfig {
  if (context.type === 'conversation') {
    return CONTEXT_CONFIG['general'];
  }
  return CONTEXT_CONFIG[context.type];
}

export function buildContextualMessage(context: VoiceContext, transcript: string): string {
  const config = getContextConfig(context);
  if (config.promptPrefix !== null) {
    return `${config.promptPrefix}\n\n${transcript}`;
  }
  return transcript;
}
